import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { CasoFolder } from '@/types/casos';
import { useToast } from '@/hooks/use-toast';

export function useCasoFolders(parentFolderId?: string | null) {
  return useQuery({
    queryKey: ['caso-folders', parentFolderId],
    queryFn: async () => {
      const params = parentFolderId ? `?parent_folder_id=${parentFolderId}` : '';
      const response = await supabase.functions.invoke(
        `caso-folders-list${params}`,
        { method: 'GET' }
      );
      if (response.error) throw response.error;
      return response.data as CasoFolder[];
    },
  });
}

export function useCreateCasoFolder() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: {
      name: string;
      parent_folder_id?: string | null;
      icon?: string;
      color?: string;
    }) => {
      const response = await supabase.functions.invoke('caso-folders-create', {
        body: data,
      });
      if (response.error) throw response.error;
      return response.data as CasoFolder;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['caso-folders'] });
      toast({ title: 'Pasta criada com sucesso!' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro ao criar pasta',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useUpdateCasoFolder() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: Partial<CasoFolder> & { id: string }) => {
      const response = await supabase.functions.invoke('caso-folders-update', {
        body: data,
      });
      if (response.error) throw response.error;
      return response.data as CasoFolder;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['caso-folders'] });
      toast({ title: 'Pasta atualizada com sucesso!' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro ao atualizar pasta',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useDeleteCasoFolder() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await supabase.functions.invoke('caso-folders-delete', {
        body: { id },
      });
      if (response.error) throw response.error;
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['caso-folders'] });
      queryClient.invalidateQueries({ queryKey: ['casos'] });
      toast({ title: 'Pasta excluída com sucesso!' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro ao excluir pasta',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useMoveCaso() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: { caso_id: string; folder_id: string | null }) => {
      const response = await supabase.functions.invoke('casos-move', {
        body: data,
      });
      if (response.error) throw response.error;
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['casos'] });
      queryClient.invalidateQueries({ queryKey: ['caso-folders'] });
      toast({ title: 'Caso movido com sucesso!' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro ao mover caso',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useFolderBreadcrumb(folderId?: string | null) {
  return useQuery({
    queryKey: ['folder-breadcrumb', folderId],
    queryFn: async () => {
      if (!folderId) return [];

      const breadcrumb: Array<{ id: string; name: string }> = [];
      let currentId: string | null = folderId;

      while (currentId) {
        const { data: folder, error } = await supabase
          .from('caso_folders')
          .select('id, name, parent_folder_id')
          .eq('id', currentId)
          .single();

        if (error || !folder) break;

        breadcrumb.unshift({ id: folder.id, name: folder.name });
        currentId = folder.parent_folder_id;
      }

      return breadcrumb;
    },
    enabled: !!folderId,
  });
}
